
import { Router, NavigationEnd } from "@angular/router";
import { DOCUMENT } from "@angular/common";
import {
  Component,
  Inject,
  ElementRef,
  OnInit,
  Renderer2,
  HostListener,
  OnDestroy,
} from "@angular/core";
import { ROUTES } from "./sidebar-items";
import { Role } from "src/app/core/models/role";
import { TokenStorageService } from "src/app/core/service/token-storage.service";
import { BaseComponent } from "src/app/shared/components/base/base.component";

@Component({
  selector: "app-sidebar",
  templateUrl: "./sidebar.component.html",
  styleUrls: ["./sidebar.component.sass"],
})
export class SidebarComponent extends BaseComponent implements OnInit, OnDestroy {
  public sidebarItems: any[];
  level1Menu = "";
  level2Menu = "";
  level3Menu = "";
  public innerHeight: any;
  public bodyTag: any;
  listMaxHeight: string;
  listMaxWidth: string;
  userFullName: string;
  userImg: string;
  userType: string;
  headerHeight = 60;
  currentRoute: string;
  routerObj = null;
  currentUser: any;

  /** Tracks the current URL so the template can highlight active items. */
  private currentUrl = "";

  constructor(
    @Inject(DOCUMENT) private document: Document,
    private renderer: Renderer2,
    public elementRef: ElementRef,
    private router: Router,
    private tokenStorageService: TokenStorageService
  ) {
    super();
    this.routerObj = this.router.events.subscribe((event) => {
      if (event instanceof NavigationEnd) {
        this.closeMobileSidebar();
        this.currentUrl = event.url.split("?")[0];

        const role = ["FARMER", "STAFF", "MANUFACTURER", "WAREHOUSE", "SERVICE_PROVIDER"];
        const firstString = this.currentUrl.split("/").slice(1)[0];

        if (role.indexOf(firstString) !== -1) {
          this.level1Menu = event.url.split("/")[2];
          this.level2Menu = event.url.split("/")[3];
        } else {
          this.level1Menu = event.url.split("/")[1];
          this.level2Menu = event.url.split("/")[2];
        }
         if (this.currentUrl.includes("/onboarding/farmers")) {
    this.level1Menu = "farmers";
  }

        this.renderer.removeClass(this.document.body, "overlay-open");
      }
    });
  }

  // ── Window resize / outside-click handlers ──────────────────

  @HostListener("window:resize", ["$event"])
  windowResizecall(event) {
    this.setMenuHeight();
    this.checkStatuForResize(false);
  }

  @HostListener("document:mousedown", ["$event"])
  onGlobalClick(event): void {
    if (!this.elementRef.nativeElement.contains(event.target)) {
      this.renderer.removeClass(this.document.body, "overlay-open");
    }
  }

  // ── Route helper used in the template ───────────────────────

  /**
   * Returns true when the given path matches (or starts with) the current URL.
   * Handles both relative ('./foo') and absolute ('/foo') paths.
   */
  isActiveRoute(path: string): boolean {
    if (!path || path === "") return false;
    const normalised = path.startsWith("./") ? path.slice(1) : path;
    return this.currentUrl === normalised || this.currentUrl.startsWith(normalised + "/");
  }

  // ── Nav click with splash ripple ────────────────────────────

  onNavClick(event: MouseEvent, item: any): void {
    // ── Splash ripple ──
    const link = event.currentTarget as HTMLElement;
    const rect = link.getBoundingClientRect();
    const size = Math.max(rect.width, rect.height);
    const x = event.clientX - rect.left - size / 2;
    const y = event.clientY - rect.top - size / 2;

    const ripple = this.renderer.createElement("span");
    this.renderer.addClass(ripple, "splash-ripple");
    this.renderer.setStyle(ripple, "width", `${size}px`);
    this.renderer.setStyle(ripple, "height", `${size}px`);
    this.renderer.setStyle(ripple, "left", `${x}px`);
    this.renderer.setStyle(ripple, "top", `${y}px`);
    this.renderer.appendChild(link, ripple);

    setTimeout(() => {
      if (link.contains(ripple)) {
        this.renderer.removeChild(link, ripple);
      }
    }, 600);

    // ── Toggle submenu ──
    if (item.submenu && item.submenu.length > 0) {
      this.callLevel1Toggle(event, item.moduleName);
    }

    if (window.innerWidth < 1170 && (!item.submenu || item.submenu.length === 0)) {
      this.closeMobileSidebar();
    }
  }

  // ── Toggle helpers ───────────────────────────────────────────

  callLevel1Toggle(event: any, element: any) {
    this.level1Menu = element === this.level1Menu ? "0" : element;
  }

  callLevel2Toggle(event: any, element: any) {
    this.level2Menu = element === this.level2Menu ? "0" : element;
  }

  callLevel3Toggle(event: any, element: any) {
    this.level3Menu = element === this.level3Menu ? "0" : element;
  }

  // ── Lifecycle ────────────────────────────────────────────────

  ngOnInit() {
    this.currentUser = this.tokenStorageService.getUser();

    if (this.currentUser) {
      const userRole = this.currentUser.role;
      this.userFullName = this.currentUser.firstName;
      this.userImg = "assets/images/prof.png";
      this.sidebarItems = ROUTES.filter((x) => x.role.indexOf(userRole) !== -1);
      if (userRole === Role.Admin) {
        this.userType = Role.Admin;
      }
    }

    this.initLeftSidebar();
    this.bodyTag = this.document.body;
  }

  ngOnDestroy() {
    this.routerObj.unsubscribe();
  }

  // ── Sidebar init ─────────────────────────────────────────────

  initLeftSidebar() {
    this.setMenuHeight();
    this.checkStatuForResize(true);
  }

  setMenuHeight() {
    this.innerHeight = window.innerHeight;
    const height = this.innerHeight - this.headerHeight;
    this.listMaxHeight = height + "";
    this.listMaxWidth = "500px";
  }

  isOpen() {
    return this.bodyTag.classList.contains("overlay-open");
  }

  checkStatuForResize(firstTime: boolean) {
    if (window.innerWidth < 1170) {
      this.renderer.addClass(this.document.body, "ls-closed");
    } else {
      this.renderer.removeClass(this.document.body, "ls-closed");
    }
  }

  // ── Hover (mini-sidebar expand) ──────────────────────────────

  mouseHover(e: any) {
    const body = this.elementRef.nativeElement.closest("body");
    if (body.classList.contains("submenu-closed")) {
      this.renderer.addClass(this.document.body, "side-closed-hover");
      this.renderer.removeClass(this.document.body, "submenu-closed");
    }
  }

  mouseOut(e: any) {
    const body = this.elementRef.nativeElement.closest("body");
    if (body.classList.contains("side-closed-hover")) {
      this.renderer.removeClass(this.document.body, "side-closed-hover");
      this.renderer.addClass(this.document.body, "submenu-closed");
    }
  }

  // ── Auth ─────────────────────────────────────────────────────

  logout(): void {
    this.tokenStorageService.signOut();
    this.router.navigate(["/authentication/signin"]);
  }

  // ── Mobile sidebar ───────────────────────────────────────────

  mobileOpen = false;

toggleMobileSidebar(): void {
  this.mobileOpen = !this.mobileOpen;
  if (this.mobileOpen) {
    this.renderer.addClass(this.document.body, 'sidebar-mobile-open');
  } else {
    this.renderer.removeClass(this.document.body, 'sidebar-mobile-open');
  }
}

closeMobileSidebar(): void {
  this.mobileOpen = false;
  this.renderer.removeClass(this.document.body, 'sidebar-mobile-open');
}
}